<template>
  <div class="space-y-6">
    <div>
      <label class="text-sm font-semibold text-zinc-700 dark:text-zinc-300">Your Prompt</label>
      <p class="mt-1 text-xs text-zinc-400 dark:text-zinc-500">Edit this before generating.</p>
      <textarea
        v-model="store.generatedText"
        rows="4"
        class="mt-3 w-full resize-none rounded-2xl border border-zinc-200 bg-surface px-4 py-3 text-sm text-zinc-800 placeholder-zinc-400 outline-none transition focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20 dark:border-white/10 dark:bg-surface-dark dark:text-zinc-100 dark:placeholder-zinc-600"
        placeholder="Your story prompt will appear here..."
      />
    </div>

    <div class="flex items-center justify-between pt-2">
      <button
        @click="$emit('back')"
        class="rounded-xl border border-zinc-200 px-5 py-2 text-sm font-medium text-zinc-500 transition hover:border-zinc-300 hover:text-zinc-900 dark:border-white/10 dark:text-zinc-400 dark:hover:border-white/20 dark:hover:text-zinc-50"
      >
        &larr; Back
      </button>

      <div class="flex items-center gap-3">
        <button
          @click="copyPrompt"
          class="rounded-xl border border-zinc-200 px-4 py-2 text-sm font-medium text-zinc-500 transition hover:border-zinc-300 hover:text-zinc-900 dark:border-white/10 dark:text-zinc-400 dark:hover:border-white/20 dark:hover:text-zinc-50"
        >
          {{ copied ? 'Copied!' : 'Copy' }}
        </button>
        <button
          @click="handleGenerate"
          :disabled="!store.generatedText.trim() || isStarting"
          class="inline-flex min-w-[10.5rem] items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-500 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-violet-500/25 transition hover:shadow-violet-500/40 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span
            v-if="isStarting"
            class="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-white/30 border-t-white"
            aria-hidden="true"
          />
          {{ isStarting ? 'Starting…' : 'Generate Story' }}
          <span v-if="!isStarting" aria-hidden="true">&rarr;</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useStoryStore } from '@/stores/story';
import { useClipboard } from '@vueuse/core';
import { computed, ref, nextTick } from 'vue';

const emit = defineEmits(['back', 'next']);
const store = useStoryStore();

const isStarting = ref(false);

const source = computed(() => store.generatedText);
const { copy, copied } = useClipboard({ source });

function copyPrompt() {
  copy(store.generatedText);
}

/**
 * Go to the result step first so the full-screen "Generating" panel can mount,
 * then run the API (was: generate on step 2 → spinner never visible).
 */
async function handleGenerate() {
  if (!store.generatedText.trim()) return;
  isStarting.value = true;
  try {
    emit('next');
    await nextTick();
    await store.generateStory();
  } finally {
    isStarting.value = false;
  }
}
</script>
